import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CEDatePicker from "components/CEDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatIndentIncreaseRoundedIcon from "@mui/icons-material/FormatIndentIncreaseRounded";
import FormatIndentDecreaseRoundedIcon from "@mui/icons-material/FormatIndentDecreaseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import Overlay from "components/Overlay/Overlay";
import api from "services/api";
import dayjs from "dayjs";
import { replacePlaceholders } from "helpers/assignmentPlaceholders";

const stripHtmlTags = (html) => {
  if (!html) return "";

  // Convert closing block-level tags to newlines (handles <p>, <div>, <li>, etc.)
  let text = html.replace(/<\/(p|div|li|tr|blockquote|h[1-6])>/gi, "\n");

  // Convert <br> to newlines
  text = text.replace(/<br\s*\/?>/gi, "\n");

  // Remove all remaining HTML tags
  text = text.replace(/<[^>]*>/g, "");

  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  // Collapse 3+ consecutive newlines to max 2 (one blank line)
  text = text.replace(/\n{3,}/g, "\n\n");

  return text.trim();
};

// Maximum number of assignment lines included in an {AssignmentsList}
// expansion. Keeps reminder SMS bodies readable and within practical
// message lengths; a "+N more" line is appended when the list is capped.
const MAX_ASSIGNMENTS_LIST_ITEMS = 10;

// Fallback per-assignment line used when no AssignmentReminderDetails
// modifier is configured for the SMS channel. Guarantees the literal
// {AssignmentsList} token is never sent to a linguist.
const ASSIGNMENTS_LIST_FALLBACK_LINE =
  "<div>{AssignmentDetails} — {AssignmentDate} {AssignmentStartTime}</div>";

// Expand {AssignmentsList} into one block per visible assignment. Uses the
// configured AssignmentReminderDetails modifier when available; otherwise
// falls back to a compact summary line so the raw placeholder token never
// reaches the linguist. The list is capped to keep the SMS readable.
const buildAssignmentsList = (assignments, modifier) => {
  const lineTemplate = modifier
    ? modifier.Body
    : ASSIGNMENTS_LIST_FALLBACK_LINE;
  const lines = assignments.map((assignment) =>
    replacePlaceholders(lineTemplate, assignment),
  );

  if (lines.length <= MAX_ASSIGNMENTS_LIST_ITEMS) {
    return lines.join("");
  }

  const capped = lines.slice(0, MAX_ASSIGNMENTS_LIST_ITEMS).join("");
  const remaining = lines.length - MAX_ASSIGNMENTS_LIST_ITEMS;
  const moreLine = `<div>+${remaining} more assignment${
    remaining > 1 ? "s" : ""
  }</div>`;
  return `${capped}${moreLine}`;
};

const getDefaultDate = () => {
  const today = dayjs();
  const dayOfWeek = today.day();

  // If Friday (5), return Friday — the reminders cover Saturday,
  // Sunday, and Monday (the three days after the selected Friday).
  if (dayOfWeek === 5) {
    return today;
  }

  // Otherwise return tomorrow
  return today.add(1, "day");
};

// Label for the date picker / empty state. On the DEFAULT Friday the
// reminders cover Saturday through Monday, so the label shows the range.
// A manually selected date (including a Friday) shows only that date.
const getDateRangeLabel = (date, isDefaultFriday = false) => {
  if (isDefaultFriday && date.day() === 5) {
    return `${date.add(1, "day").format("dddd, MMMM DD")} – ${date
      .add(3, "day")
      .format("dddd, MMMM DD, YYYY")}`;
  }
  return date.format("MMMM DD, YYYY");
};

// Resolve the dialable phone number for a linguist from the dedicated
// phone-number table (primary first, then first entry), falling back to
// the legacy virtual field. Returns digits only (with + country prefix
// when the country code is not US/Canada) so it is safe for sms:/tel: links.
const resolveLinguistPhone = (linguist) => {
  const phones = Array.isArray(linguist?.PhoneNumbers)
    ? linguist.PhoneNumbers.filter((p) => String(p?.Number || "").trim())
    : [];
  if (phones.length > 0) {
    const primary = phones.find((p) => p.IsPrimary) || phones[0];
    const cc = String(primary?.CountryCode || "").trim();
    const withPrefix =
      cc && cc !== "1" ? `+${cc}${primary.Number}` : primary.Number;
    return withPrefix.replace(/[^0-9+]/g, "");
  }
  const raw = String(linguist?.PhoneNumber || "").trim();
  return raw ? raw.replace(/[^0-9+]/g, "") : "";
};

const getAssignmentStatus = (assignment) => {
  return (assignment?.InterpretationStatus || "")
    .toString()
    .trim();
};

const normalizeStatus = (status) =>
  status
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const isCancelledAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  return status.startsWith("cancelled") || status.includes("cancelled");
};

const isCompletedAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  return status.includes("completed");
};

const isConfirmedAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  if (!status) return false;
  if (isCancelledAssignment(assignment) || isCompletedAssignment(assignment)) {
    return false;
  }

  return status.includes("confirmed");
};

const isRequestedAssignment = (assignment) => {
  const status = normalizeStatus(getAssignmentStatus(assignment));
  if (!status) return false;
  if (isCancelledAssignment(assignment) || isCompletedAssignment(assignment)) {
    return false;
  }

  return status.includes("requested");
};

const getVisibleAssignmentsForLinguist = (linguist, excludeRequested) => {
  const confirmedAssignments = linguist?.confirmedAssignments || [];
  const requestedAssignments = linguist?.requestedAssignments || [];

  return excludeRequested
    ? confirmedAssignments
    : [...confirmedAssignments, ...requestedAssignments];
};

const InterpretationReminders = ({ initialDate }) => {
  usePageTitle("Interpretation Reminders");

  const theme = useTheme();
  const { setOverlayName, showSnackbar, openDialog, closeDialog, overlayData } =
    useStateContext();

  // When opened from a reminder_watch notification, the target date arrives
  // via overlayData.initialDate (set by the notification click handler) or
  // the optional initialDate prop. A date supplied this way is treated as a
  // manual selection — the Friday → weekend expansion applies only to the
  // default date.
  const initialDateFromContext = useMemo(() => {
    const candidate =
      overlayData?.initialDate || overlayData?.date || initialDate;
    if (!candidate) return null;
    const parsed = dayjs(candidate);
    return parsed.isValid() ? parsed : null;
  }, [overlayData?.initialDate, overlayData?.date, initialDate]);

  const [selectedDate, setSelectedDate] = useState(
    () => initialDateFromContext || getDefaultDate(),
  );
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [assignmentModifier, setAssignmentModifier] = useState(null);
  const [smsModifiers, setSmsModifiers] = useState([]);
  const [linguists, setLinguists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingLinguistId, setEditingLinguistId] = useState(null);
  const [savedRange, setSavedRange] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [excludeRequestedGlobal, setExcludeRequestedGlobal] = useState(true);
  const editorRef = useRef(null);

  // True once the user manually picks a date. The Friday → weekend expansion
  // is a DEFAULT-date behavior only — a manually selected date (including a
  // Friday) must show exactly that day's assignments.
  const dateManuallyChangedRef = useRef(Boolean(initialDateFromContext));

  // Portal-styled border for the date field: primary when a date is set,
  // grey when empty, error when invalid. Matches TranslationDocuments.
  const getTextFieldBorderSx = useCallback(
    (value, hasError = false) => {
      const isFilled = value != null && String(value).trim() !== "";
      const borderColor = hasError
        ? theme.palette.error.main
        : isFilled
          ? theme.palette.primary.main
          : theme.palette.grey[400];

      return {
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: hasError
              ? theme.palette.error.main
              : isFilled
                ? theme.palette.primary.main
                : theme.palette.grey[500],
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor,
            borderWidth: 2,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
          },
        },
      };
    },
    [theme],
  );

  // Tracks templates the user manually picked per linguist. These overrides
  // survive re-fetches/re-renders so auto-selection never overwrites a
  // user's explicit choice.
  const userTemplateOverridesRef = useRef({});

  // Stable snackbar accessor so the fetch effect below doesn't re-run when
  // the context re-renders (e.g. after Copy/Send snackbars).
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const isSettingsMenuOpen = Boolean(menuAnchorEl);

  const getVisibleAssignments = useCallback(
    (linguist) =>
      getVisibleAssignmentsForLinguist(linguist, excludeRequestedGlobal),
    [excludeRequestedGlobal],
  );

  const displayedLinguists = useMemo(
    () =>
      linguists.filter(
        (linguist) => getVisibleAssignments(linguist).length > 0,
      ),
    [linguists, getVisibleAssignments],
  );

  const closeOverlay = () => setOverlayName("");

  const handleSettingsMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleExcludeRequestedToggle = (checked) => {
    setExcludeRequestedGlobal(checked);

    if (!editingLinguistId) return;

    const editingLinguist = linguists.find((ling) => ling.id === editingLinguistId);
    if (!editingLinguist) {
      setEditingLinguistId(null);
      return;
    }

    const visibleAssignments = getVisibleAssignmentsForLinguist(
      editingLinguist,
      checked,
    );

    if (visibleAssignments.length === 0) {
      setEditingLinguistId(null);
    }
  };

  const handleModifierMouseDown = (e) => {
    e.preventDefault();
    // Save the current selection before the click
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0));
    }
  };

  const insertModifier = (modifier) => {
    const el = editorRef.current;
    if (!el) {
      console.error("Editor ref is not available");
      return;
    }

    // Focus the editor
    el.focus();

    // Restore the saved selection if available
    if (savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange);
      setSavedRange(null);
    }

    // Insert at cursor position using execCommand
    document.execCommand("insertHTML", false, modifier.Body);

    // Re-run placeholder replacement on the updated content
    const updatedContent = el.innerHTML;
    const currentLinguist = linguists.find((l) => l.id === editingLinguistId);
    const visibleAssignments = currentLinguist
      ? getVisibleAssignments(currentLinguist)
      : [];

    if (currentLinguist && visibleAssignments.length > 0) {
      const replacedContent = replacePlaceholders(
        updatedContent,
        visibleAssignments[0],
      );
      el.innerHTML = replacedContent;

      // Move cursor to end after innerHTML change (since range positions are now invalid)
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Update the edited message in state immediately with replaced content
      setLinguists((prev) =>
        prev.map((ling) =>
          ling.id === editingLinguistId
            ? {
                ...ling,
                editedMessage: el.innerHTML,
                isCopied: false,
                isSent: false,
              }
            : ling,
        ),
      );
    }
  };

  // Fetch SMS templates and modifiers on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const [
          smsTemplatesResponse,
          allTemplatesResponse,
          modifierResponse,
          allModifiersResponse,
        ] = await Promise.all([
          api.get("/communicationTemplates?channel=SMS"),
          api.get("/communicationTemplates"),
          api.get(
            "/communicationTemplates?channel=SMS&type=Modifier&name=AssignmentReminderDetails",
          ),
          api.get("/communicationTemplates?type=Modifier"),
        ]);

        // Prefer SMS-channel templates, but fall back to all templates when
        // none exist with Channel=SMS (templates are created with the default
        // Email channel in Settings). Without the fallback the dropdown would
        // be empty. The dropdown still filters to Template type only.
        const smsOnly = smsTemplatesResponse.data || [];
        const templates =
          smsOnly.length > 0 ? smsOnly : allTemplatesResponse.data || [];
        setSmsTemplates((prev) =>
          JSON.stringify(prev) === JSON.stringify(templates)
            ? prev
            : templates,
        );
        setSmsModifiers((prev) =>
          JSON.stringify(prev) === JSON.stringify(allModifiersResponse.data || [])
            ? prev
            : allModifiersResponse.data || [],
        );

        // Get the first modifier that matches (or find by name)
        const modifiers = modifierResponse.data || [];
        const modifier =
          modifiers.find((m) => m.Name === "AssignmentReminderDetails") ||
          modifiers[0];
        setAssignmentModifier((prev) =>
          JSON.stringify(prev) === JSON.stringify(modifier) ? prev : modifier,
        );
      } catch (err) {
        console.error("Error fetching SMS templates:", err);
        showSnackbarRef.current("Error loading SMS templates", theme, "error");
      }
    };

    fetchTemplates();
    // Run once on mount only — a changing showSnackbar/theme identity used to
    // re-fetch templates, which re-created the smsTemplates array and caused
    // the linguists list to refresh a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch linguists with assignments when date or smsTemplates change
  useEffect(() => {
    if (!selectedDate || smsTemplates.length === 0) return;

    const fetchLinguistsWithAssignments = async () => {
      setLoading(true);
      try {
        // On the DEFAULT Friday the reminders cover Saturday, Sunday, and
        // Monday — fetch all three dates and merge. A manually selected
        // date (including a Friday) fetches exactly the selected date.
        // Key off TODAY being Friday, not the selected date: on Thursday
        // the default selected date IS Friday (tomorrow), and expanding
        // then would fetch Sat/Sun/Mon instead of Friday's assignments.
        const isDefaultFriday =
          dayjs().day() === 5 && !dateManuallyChangedRef.current;
        const datesToFetch = isDefaultFriday
          ? [
              selectedDate.add(1, "day"),
              selectedDate.add(2, "day"),
              selectedDate.add(3, "day"),
            ]
          : [selectedDate];

        const responses = await Promise.all(
          datesToFetch.map((d) =>
            api.get(
              `/services/by-date?date=${d.format("YYYY-MM-DD")}&serviceCategory=Interpretation`,
            ),
          ),
        );

        // Merge assignments from all fetched dates
        const allAssignments = responses.flatMap((r) => r.data || []);

        // Group assignments by linguist and auto-select template
        const linguistMap = new Map();

        allAssignments.forEach((assignment) => {
          const linguistId = assignment.Linguist?.id;
          if (!linguistId) return;

          if (!linguistMap.has(linguistId)) {
            linguistMap.set(linguistId, {
              id: linguistId,
              name: assignment.Linguist.Name,
              phoneNumber: resolveLinguistPhone(assignment.Linguist),
              confirmedAssignments: [],
              requestedAssignments: [],
              hasRequestedAssignments: false,
              selectedTemplateId: null, // will set after grouping
              editedMessage: null,
              isCopied: false,
              isSent: false,
            });
          }

          const linguistEntry = linguistMap.get(linguistId);

          if (isConfirmedAssignment(assignment)) {
            linguistEntry.confirmedAssignments.push(assignment);
          } else if (isRequestedAssignment(assignment)) {
            linguistEntry.requestedAssignments.push(assignment);
            linguistEntry.hasRequestedAssignments = true;
          }
        });

        // For each linguist, pick the correct template based on first assignment's InterpretationType
        const updatedLinguists = Array.from(linguistMap.values())
          .filter(
            (ling) =>
              ling.confirmedAssignments.length > 0 ||
              ling.requestedAssignments.length > 0,
          )
          .map((ling) => {
            const visibleAssignments = getVisibleAssignmentsForLinguist(
              ling,
              excludeRequestedGlobal,
            );
            const fallbackAssignments = [
              ...ling.confirmedAssignments,
              ...ling.requestedAssignments,
            ];
            const firstAssignment =
              visibleAssignments[0] || fallbackAssignments[0] || null;

            // Rule-based template auto-selection (same logic as Send Details):
            // evaluate each template's MatchRules against the assignment and
            // pick the most specific match. Falls back to the first template.
            let selectedTemplate = null;
            let bestRuleRank = null;

            const assignmentValues = {
              Client: firstAssignment?.Client?.Account || "",
              Requestor: firstAssignment?.Requestor?.Name || "",
              Linguist: firstAssignment?.Linguist?.Name || "",
              InterpretationType:
                firstAssignment?.ServiceSubtype?.Name ||
                firstAssignment?.InterpretationType ||
                "",
              Language:
                firstAssignment?.ServiceTargetLanguages?.[0]?.Language
                  ?.Name || "",
              Mode: firstAssignment?.Mode || "",
              ServiceCategory: firstAssignment?.ServiceCategory || "",
              InterpretationStatus:
                firstAssignment?.InterpretationStatus || "",
              Name: firstAssignment?.Name || "",
              Details: firstAssignment?.Details || "",
              Address: firstAssignment?.Address || "",
              ProjectNumber: firstAssignment?.ProjectNumber || "",
            };

            const RULE_FIELD_SPECIFICITY = {
              Client: 10,
              Linguist: 12,
              Requestor: 14,
              InterpretationType: 20,
              Language: 30,
              Mode: 40,
              ServiceCategory: 50,
              Name: 60,
              ProjectNumber: 65,
              Details: 70,
              Address: 80,
              InterpretationStatus: 90,
            };

            const matchesRule = (rule, actualValue) => {
              const ruleValue = String(rule?.Value || "").trim();
              const actual = String(actualValue || "").trim();
              if (!ruleValue || !actual) return false;
              if (rule?.MatchType === "contains") {
                return actual.toLowerCase().includes(ruleValue.toLowerCase());
              }
              return actual.toLowerCase() === ruleValue.toLowerCase();
            };

            // Only Template-type records are selectable in the dropdown —
            // never evaluate Modifiers (they are not shown and would produce
            // an out-of-range selection).
            const selectableTemplates = smsTemplates.filter(
              (t) => t.Type === "Template" || !t.Type,
            );

            selectableTemplates.forEach((template) => {
              (template.MatchRules || []).forEach((rule) => {
                const actualValue = assignmentValues[rule.Field];
                if (
                  actualValue === undefined ||
                  !matchesRule(rule, actualValue)
                ) {
                  return;
                }
                const specificity =
                  RULE_FIELD_SPECIFICITY[rule.Field] ?? 100;
                const rank = specificity * 1000 + (Number(rule.Priority) || 0);
                if (bestRuleRank === null || rank < bestRuleRank) {
                  bestRuleRank = rank;
                  selectedTemplate = template;
                }
              });
            });

            if (!selectedTemplate) {
              selectedTemplate = selectableTemplates[0];
            }

            // If the user manually picked a template for this linguist,
            // keep their choice — auto-selection only applies as the
            // initial default.
            const userOverride =
              userTemplateOverridesRef.current[ling.id] ?? null;

            // Reminder tracking: allReminded is true when EVERY assignment
            // (confirmed + requested) already has a ReminderSentOn timestamp.
            // hasRemindedAssignments flags a mix (some reminded, some not).
            const linguistAssignments = [
              ...ling.confirmedAssignments,
              ...ling.requestedAssignments,
            ];
            const allReminded =
              linguistAssignments.length > 0 &&
              linguistAssignments.every((a) => !!a.ReminderSentOn);
            const hasRemindedAssignments = linguistAssignments.some(
              (a) => !!a.ReminderSentOn,
            );

            return {
              ...ling,
              allReminded,
              hasRemindedAssignments,
              selectedTemplateId: userOverride
                ? userOverride
                : selectedTemplate
                  ? selectedTemplate.id
                  : selectableTemplates[0]?.id || null,
            };
          });

        setLinguists(updatedLinguists);
      } catch (err) {
        console.error("Error fetching assignments:", err);
        showSnackbarRef.current("Error loading assignments", theme, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchLinguistsWithAssignments();
  }, [selectedDate, smsTemplates, excludeRequestedGlobal, theme]);

  const generateMessage = useCallback(
    (linguist, htmlMode = true) => {
      if (linguist.editedMessage) return linguist.editedMessage;

      const template = smsTemplates.find(
        (t) => t.id === linguist.selectedTemplateId,
      );
      const visibleAssignments = getVisibleAssignments(linguist);
      if (!template || visibleAssignments.length === 0) return "";

      let bodyText = htmlMode ? template.Body : stripHtmlTags(template.Body);

      // Handle {NumberOfAssignments}
      bodyText = bodyText.replace(
        /{NumberOfAssignments}/g,
        visibleAssignments.length.toString(),
      );

      // Handle {AssignmentsList} — always expand so the literal token is
      // never sent. Uses the AssignmentReminderDetails modifier when
      // configured; falls back to a compact summary line otherwise.
      if (bodyText.includes("{AssignmentsList}")) {
        bodyText = bodyText.replace(
          /{AssignmentsList}/g,
          buildAssignmentsList(visibleAssignments, assignmentModifier),
        );
      }

      // Replace other placeholders using first assignment
      const firstAssignment = visibleAssignments[0];
      return replacePlaceholders(bodyText, firstAssignment);
    },
    [smsTemplates, assignmentModifier, getVisibleAssignments],
  );

  // Update editor content when template changes while editing
  useEffect(() => {
    if (editingLinguistId && editorRef.current) {
      const linguist = linguists.find((l) => l.id === editingLinguistId);
      if (linguist) {
        // Only update if we don't have an edited message (i.e., template just changed)
        if (!linguist.editedMessage) {
          const message = generateMessage(linguist, true);
          editorRef.current.innerHTML = message;
        }
      }
    }
  }, [editingLinguistId, smsTemplates, linguists, generateMessage]);

  const handleTemplateChange = (linguistId, templateId) => {
    // Record the user's explicit choice so re-fetches/re-renders keep it.
    userTemplateOverridesRef.current[linguistId] = templateId;

    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === linguistId
          ? {
              ...ling,
              selectedTemplateId: templateId,
              editedMessage: null,
              isCopied: false,
              isSent: false,
            }
          : ling,
      ),
    );
  };

  // Update editor content when template changes while editing
  useEffect(() => {
    if (!editingLinguistId || !editorRef.current) return;
    const linguist = linguists.find((l) => l.id === editingLinguistId);
    if (!linguist || linguist.editedMessage) return;

    const message = generateMessage(linguist, true);
    if (editorRef.current.innerHTML !== message) {
      editorRef.current.innerHTML = message;
    }
  }, [editingLinguistId, linguists, generateMessage]);

  const handleEdit = (linguistId) => {
    setEditingLinguistId(linguistId);
    const linguist = linguists.find((l) => l.id === linguistId);

    setTimeout(() => {
      if (editorRef.current && linguist) {
        const message =
          linguist.editedMessage || generateMessage(linguist, true);
        editorRef.current.innerHTML = message;
      }
    }, 0);
  };

  const handleDoneEdit = () => {
    const linguist = linguists.find((l) => l.id === editingLinguistId);
    if (!linguist) return;

    // Check if there are any unhandled manual replacements
    const currentMessage =
      linguist.editedMessage || generateMessage(linguist, true);
    if (currentMessage.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (currentMessage.match(/NO_DATA_REPLACE_MANUALLY/g) || [])
        .length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't save until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    // Save the changes and exit edit mode
    setEditingLinguistId(null);
  };

  const handleCancelEdit = () => {
    // Discard changes and exit edit mode
    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === editingLinguistId
          ? { ...ling, editedMessage: null, isCopied: false, isSent: false }
          : ling,
      ),
    );
    setEditingLinguistId(null);
  };

  const handleBodyInput = (e) => {
    const htmlContent = e.currentTarget?.innerHTML;
    if (htmlContent !== undefined) {
      setLinguists((prev) =>
        prev.map((ling) =>
          ling.id === editingLinguistId
            ? {
                ...ling,
                editedMessage: htmlContent,
                isCopied: false,
                isSent: false,
              }
            : ling,
        ),
      );
    }
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleOpenLinkDialog = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0));
    }

    openDialog({
      theme: theme,
      title: "Insert Hyperlink",
      message: "",
      customContent: (
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateLink();
              }
            }}
            autoFocus
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Cancel",
      primaryAction: "Insert Link",
      tertiaryOnClick: null,
      secondaryOnClick: () => {
        closeDialog();
        setLinkUrl("");
        setSavedSelection(null);
      },
      primaryOnClick: () => {
        handleCreateLink();
      },
    });
  };

  const handleCloseLinkDialog = () => {
    closeDialog();
    setLinkUrl("");
    setSavedSelection(null);
  };

  const handleCreateLink = () => {
    if (!linkUrl.trim()) {
      showSnackbar("Please enter a URL", theme, "error");
      return;
    }

    const el = editorRef.current;
    if (!el) return;

    el.focus();

    // Restore the saved selection
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }

    // Create link with the provided URL
    let url = linkUrl.trim();
    // Add protocol if not present
    if (!url.match(/^https?:\/\//)) {
      url = "https://" + url;
    }

    document.execCommand("createLink", false, url);

    // Sync the edited message state (same update as the editor's onInput)
    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === editingLinguistId
          ? {
              ...ling,
              editedMessage: el.innerHTML,
              isCopied: false,
              isSent: false,
            }
          : ling,
      ),
    );

    handleCloseLinkDialog();
  };

  // Shared fire-and-forget tracking: record that reminders were sent for
  // these assignments so the Reminded indicator reflects server state.
  // Never blocks the caller — failures are logged and ignored.
  const markAssignmentsReminded = (linguistId, assignmentIds) => {
    if (!assignmentIds || assignmentIds.length === 0) return;

    api
      .post("/services/reminders/markSent", { serviceIds: assignmentIds })
      .then(() => {
        // Update local assignment objects so the Reminded indicator
        // reflects it (all of this linguist's assignments were marked).
        setLinguists((prev) =>
          prev.map((ling) =>
            ling.id === linguistId
              ? {
                  ...ling,
                  allReminded: true,
                  hasRemindedAssignments: true,
                  confirmedAssignments: (ling.confirmedAssignments || []).map(
                    (a) =>
                      assignmentIds.includes(a.id)
                        ? { ...a, ReminderSentOn: new Date().toISOString() }
                        : a,
                  ),
                  requestedAssignments: (ling.requestedAssignments || []).map(
                    (a) =>
                      assignmentIds.includes(a.id)
                        ? { ...a, ReminderSentOn: new Date().toISOString() }
                        : a,
                  ),
                }
              : ling,
          ),
        );
      })
      .catch((err) =>
        console.error("Failed to mark reminders as sent:", err),
      );
  };

  const handleCopy = async (linguistId) => {
    const linguist = linguists.find((l) => l.id === linguistId);
    if (!linguist) return;

    // Generate message to copy
    const htmlMessage =
      linguist.editedMessage || generateMessage(linguist, true);

    // Check if there are any unhandled manual replacements
    if (htmlMessage.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (htmlMessage.match(/NO_DATA_REPLACE_MANUALLY/g) || [])
        .length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't copy until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    // Generate plain text version (strip HTML) for copying
    const plainTextMessage = stripHtmlTags(htmlMessage);

    try {
      await navigator.clipboard.writeText(plainTextMessage);

      setLinguists((prev) =>
        prev.map((ling) =>
          ling.id === linguistId
            ? {
                ...ling,
                isCopied: true,
                // Phone-less linguists have no sms: flow, so the copy IS
                // the reminder send — show the Sent chip too.
                ...(!linguist.phoneNumber ? { isSent: true } : {}),
              }
            : ling,
        ),
      );

      // Copying the message counts as delivering the reminder — the user
      // pastes it into whatever channel they use (WhatsApp, email, …) — so
      // record it server-side exactly like handleSend does (2026-09-23,
      // per Amine: the assignment must be marked as reminded on copy too).
      // Previously this only ran for phone-less linguists; now every copy
      // marks the linguist's assignments as reminded.
      const assignmentIds = [
        ...(linguist.confirmedAssignments || []),
        ...(linguist.requestedAssignments || []),
      ]
        .map((a) => a.id)
        .filter(Boolean);

      markAssignmentsReminded(linguistId, assignmentIds);

      showSnackbar("Message copied to clipboard", theme, "success");
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      showSnackbar("Failed to copy message", theme, "error");
    }
  };

  const handleSend = (linguistId) => {
    const linguist = linguists.find((l) => l.id === linguistId);
    if (!linguist) return;

    // Block phone-less linguists: with no dialable number the sms: link
    // would be malformed (sms:;?&body=...). Mirror the outreach list pattern
    // and never mark the linguist as Sent.
    if (!linguist.phoneNumber) {
      showSnackbar("This linguist has no phone number", theme, "error");
      return;
    }

    const htmlMessage =
      linguist.editedMessage || generateMessage(linguist, true);

    if (htmlMessage.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (htmlMessage.match(/NO_DATA_REPLACE_MANUALLY/g) || [])
        .length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't send until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    const plainTextMessage = stripHtmlTags(htmlMessage);
    const smsLink = `sms:${linguist.phoneNumber};?&body=${encodeURIComponent(plainTextMessage)}`;
    window.open(smsLink, "_blank", "noopener");

    setLinguists((prev) =>
      prev.map((ling) =>
        ling.id === linguistId ? { ...ling, isSent: true } : ling,
      ),
    );

    // Fire-and-forget tracking: record that reminders were sent for these
    // assignments so the Reminded indicator reflects server state. Never
    // blocks the sms: flow — failures are logged and ignored.
    const assignmentIds = [
      ...(linguist.confirmedAssignments || []),
      ...(linguist.requestedAssignments || []),
    ]
      .map((a) => a.id)
      .filter(Boolean);

    markAssignmentsReminded(linguistId, assignmentIds);
  };

  // Header action — reminder settings menu. The legacy header Box (back +
  // title) is replaced by the template header; the back button keeps the
  // edit-mode behaviour via onClose (cancel edit when editing, else close).
  const headerActions = (
    <>
      <IconButton
        aria-label="Reminder settings"
        onClick={handleSettingsMenuOpen}
      >
        <MoreVertRoundedIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={isSettingsMenuOpen}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem disableRipple sx={{ cursor: "default" }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={excludeRequestedGlobal}
                onChange={(e) =>
                  handleExcludeRequestedToggle(e.target.checked)
                }
              />
            }
            label="Exclude Requested"
            sx={{ m: 0 }}
          />
        </MenuItem>
        <MenuItem disableRipple sx={{ cursor: "default", py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Requested assignments will be {excludeRequestedGlobal ? "excluded" : "included"}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Overlay
        id="interpretation-reminders"
        open={true}
        onClose={editingLinguistId ? handleCancelEdit : closeOverlay}
        title="Interpretation Reminders"
        maxWidthPx={1400}
        headerActions={headerActions}
        scrollable={false}
      >
        {/* Content wrapper — keeps the legacy shell padding so the grey body
            panel stays inset exactly as before; the body owns its scroll. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            minHeight: 0,
            p: "0rem .2rem 1.5rem 0",
          }}
        >

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: "1rem 2.3rem 1rem 2.5rem",
              height: "100%",
              width: "100%",
              overflowY: "auto",
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.primary.border2}`,
              borderRadius: "1.5rem",
            }}
          >
            <Grid container spacing={2}>
              {/* Date Picker */}
              <Grid size={{ xs: 12 }}>
                <CEDatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(newValue) => {
                    dateManuallyChangedRef.current = true;
                    setSelectedDate(newValue);
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: getTextFieldBorderSx(selectedDate),
                    },
                  }}
                />
              </Grid>

              {/* Linguists List */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    //maxHeight: "600px",
                    overflowY: "auto",
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : displayedLinguists.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1" color="text.secondary">
                        No assignments found for{" "}
                        {getDateRangeLabel(
                          selectedDate,
                          dayjs().day() === 5 &&
                            !dateManuallyChangedRef.current,
                        )}
                      </Typography>
                    </Box>
                  ) : editingLinguistId ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {displayedLinguists
                        .filter((l) => l.id === editingLinguistId)
                        .map((linguist) => (
                          <Box
                            key={linguist.id}
                            sx={
                              {
                                //p: 2,
                                //borderRadius: "12px",
                                //border: `2px solid ${theme.palette.primary.main}`,
                                //bgcolor: theme.palette.background.default,
                              }
                            }
                          >
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              gutterBottom
                            >
                              Edit Reminder for {linguist.name}
                            </Typography>

                            <Alert severity="warning" sx={{ mb: 2 }}>
                              This message will not be saved. Copy and send it
                              to the linguist right after making edits.
                            </Alert>

                            {/* Template Select during editing */}
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="SMS Template"
                                variant="outlined"
                                value={linguist.selectedTemplateId || ""}
                                onChange={(e) =>
                                  handleTemplateChange(
                                    linguist.id,
                                    e.target.value,
                                  )
                                }
                                disabled={smsTemplates.length === 0}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#ccc",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#999",
                                    },
                                  },
                                }}
                              >
                                {smsTemplates
                                  .filter(
                                    (t) => t.Type === "Template" || !t.Type,
                                  )
                                  .map((template) => (
                                    <MenuItem
                                      key={template.id}
                                      value={template.id}
                                    >
                                      {template.Name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid size={{ xs: 12, md: 8 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    Message
                                  </Typography>

                                  {/* Formatting Toolbar */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.5,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Tooltip title="Bold">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("bold")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatBoldRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Italic">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("italic")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatItalicRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Underline">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("underline")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatUnderlinedRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                                    <Tooltip title="Bullet List">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("insertUnorderedList")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatListBulletedRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Numbered List">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("insertOrderedList")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatListNumberedRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                                    <Tooltip title="Indent Right">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("indent")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatIndentIncreaseRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Indent Left">
                                      <IconButton
                                        size="small"
                                        onClick={() => applyFormat("outdent")}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <FormatIndentDecreaseRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                                    <Tooltip title="Insert Hyperlink">
                                      <IconButton
                                        size="small"
                                        onClick={handleOpenLinkDialog}
                                        sx={{ borderRadius: "8px" }}
                                      >
                                        <LinkRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                <Box
                                  ref={editorRef}
                                  contentEditable
                                  suppressContentEditableWarning
                                  onInput={handleBodyInput}
                                  sx={{
                                    height: 340,
                                    maxHeight: 340,
                                    p: 1.5,
                                    borderRadius: "12px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    bgcolor: theme.palette.background.default,
                                    outline: "none",
                                    "&:focus": {
                                      borderColor: theme.palette.primary.main,
                                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
                                    },
                                    overflowY: "auto",
                                    whiteSpace: "pre-wrap",
                                    "& ul, & ol": {
                                      paddingLeft: "1.5rem",
                                    },
                                    "& li": {
                                      marginBottom: "0.25rem",
                                    },
                                  }}
                                />
                              </Grid>

                              <Grid size={{ xs: 12, md: 4 }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Modifiers
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    p: 1.5,
                                    borderRadius: "12px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    bgcolor: theme.palette.background.default,
                                    height: 340,
                                    maxHeight: 340,
                                    overflowY: "auto",
                                  }}
                                >
                                  {smsModifiers.length > 0 ? (
                                    <>
                                      {/* Group modifiers by channel */}
                                      {["SMS", "Email"].map((channel) => {
                                        const channelModifiers =
                                          smsModifiers.filter(
                                            (m) =>
                                              m.Type === "Modifier" &&
                                              m.Channel === channel,
                                          );

                                        if (channelModifiers.length === 0)
                                          return null;

                                        return (
                                          <Box key={channel}>
                                            <Typography
                                              variant="caption"
                                              fontWeight={700}
                                              sx={{
                                                color:
                                                  theme.palette.text.secondary,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                mb: 0.5,
                                                display: "block",
                                              }}
                                            >
                                              {channel}
                                            </Typography>
                                            {channelModifiers.map(
                                              (modifier) => (
                                                <Box
                                                  key={modifier.id}
                                                  onMouseDown={
                                                    handleModifierMouseDown
                                                  }
                                                  onClick={() =>
                                                    insertModifier(modifier)
                                                  }
                                                  sx={{
                                                    border: `1px solid ${theme.palette.primary.border2}`,
                                                    borderRadius: "10px",
                                                    p: 1,
                                                    mb: 1,
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                      borderColor:
                                                        theme.palette.primary
                                                          .main,
                                                      bgcolor: `${theme.palette.primary.main}0a`,
                                                    },
                                                  }}
                                                >
                                                  <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                  >
                                                    {modifier.Name}
                                                  </Typography>
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No modifiers available
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                sx={{ borderRadius: "8px" }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleDoneEdit}
                                sx={{ borderRadius: "8px" }}
                              >
                                Done
                              </Button>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {displayedLinguists.map((linguist) => {
                        const visibleAssignments = getVisibleAssignments(linguist);
                        const requestedCount = (linguist.requestedAssignments || []).length;

                        return (
                        <Box
                          key={linguist.id}
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            {/* Linguist Info */}
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {linguist.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {visibleAssignments.length} assignment
                                {visibleAssignments.length !== 1 ? "s" : ""}
                                {requestedCount > 0
                                  ? ` (${requestedCount} Requested)`
                                  : ""}
                              </Typography>
                            </Grid>

                            {/* Template Select */}
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="SMS Template"
                                variant="outlined"
                                value={linguist.selectedTemplateId || ""}
                                onChange={(e) =>
                                  handleTemplateChange(
                                    linguist.id,
                                    e.target.value,
                                  )
                                }
                                disabled={smsTemplates.length === 0}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#ccc",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#999",
                                    },
                                  },
                                }}
                              >
                                {smsTemplates
                                  .filter(
                                    (t) => t.Type === "Template" || !t.Type,
                                  )
                                  .map((template) => (
                                    <MenuItem
                                      key={template.id}
                                      value={template.id}
                                    >
                                      {template.Name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>

                            {/* Action Buttons */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditRoundedIcon />}
                                  onClick={() => handleEdit(linguist.id)}
                                  disabled={!linguist.selectedTemplateId}
                                  sx={{ borderRadius: "8px" }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ContentCopyRoundedIcon />}
                                  onClick={() => handleCopy(linguist.id)}
                                  disabled={!linguist.selectedTemplateId}
                                  sx={{ borderRadius: "8px" }}
                                >
                                  Copy
                                </Button>
                                <Tooltip
                                  title={
                                    linguist.phoneNumber
                                      ? "Send SMS"
                                      : "This linguist has no phone number"
                                  }
                                  placement="top"
                                >
                                  <Box
                                    component="span"
                                    sx={{ display: "inline-flex" }}
                                  >
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleSend(linguist.id)}
                                      disabled={
                                        !linguist.phoneNumber ||
                                        !linguist.selectedTemplateId
                                      }
                                      sx={{ borderRadius: "8px" }}
                                    >
                                      Send
                                    </Button>
                                  </Box>
                                </Tooltip>
                              </Box>
                            </Grid>

                            {/* Status */}
                            <Grid size={{ xs: 12, md: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                }}
                              >
                                {linguist.isSent ? (
                                  <Chip
                                    icon={<CheckCircleRoundedIcon />}
                                    label="Sent"
                                    color="success"
                                    size="small"
                                  />
                                ) : linguist.isCopied ? (
                                  <Chip
                                    icon={<CheckCircleRoundedIcon />}
                                    label="Copied"
                                    color="success"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    label="Pending"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      bgcolor: "white",
                                      color: theme.palette.text.secondary,
                                    }}
                                  />
                                )}
                                {linguist.allReminded ? (
                                  <Chip
                                    icon={<CheckCircleRoundedIcon />}
                                    label="Reminded"
                                    color="success"
                                    size="small"
                                  />
                                ) : linguist.hasRemindedAssignments ? (
                                  <Chip
                                    label="Partially reminded"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      bgcolor: "white",
                                      color: theme.palette.text.secondary,
                                    }}
                                  />
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Overlay>
    </LocalizationProvider>
  );
};

export default InterpretationReminders;
